import { useCallback, useEffect, useMemo, useState } from 'react';
import { pageAttachmentIds } from '@eaco/forms-core';
import { useFormFileTransport, type ResolvedFile } from '@eaco/forms-ui-web';
import type { PageReadOnlyFile } from '@eaco/forms-ui-web/readonly';

/**
 * Every file a page's media blocks point at, resolved up front through the
 * portal's file transport (the owner's, or the share link's with its password)
 * for PageReadOnly's `resolveFile`. `ready` turns true once every lookup has
 * settled, so a render target can wait for the files instead of guessing.
 */
export function usePageFiles(pages: { elements: unknown[] }[]): {
  resolveFile: (attachmentId: number) => PageReadOnlyFile | null;
  ready: boolean;
} {
  const transport = useFormFileTransport();
  const ids = useMemo(
    () => [...new Set(pages.flatMap((page) => pageAttachmentIds({ elements: page.elements })))],
    [pages],
  );
  const key = ids.join(',');
  const [state, setState] = useState<{ key: string; files: Map<number, PageReadOnlyFile> } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        transport
          .fetchResultFileUrl(id)
          .then((file: ResolvedFile | null) => [id, file] as const)
          .catch(() => [id, null] as const),
      ),
    ).then((rows) => {
      if (cancelled) return;
      const files = new Map<number, PageReadOnlyFile>();
      for (const [id, f] of rows) {
        if (f?.url) {
          files.set(id, {
            url: f.url,
            name: f.name,
            mimeType: f.mimeType ?? null,
            size: f.sizeBytes ?? null,
            thumbnailUrl: f.thumbnailUrl ?? null,
          });
        }
      }
      setState({ key, files });
    });
    return () => {
      cancelled = true;
    };
    // `key` stands for `ids`, which is a new array whenever `pages` is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transport, key]);

  const files = state?.key === key ? state.files : null;
  const resolveFile = useCallback((id: number) => files?.get(id) ?? null, [files]);
  return { resolveFile, ready: files !== null };
}
